import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Phone, Mail, Building2, FileText, MapPin, Filter, ChevronDown, ChevronRight,
  RefreshCw, Calendar, CreditCard, Clock, CheckCircle2, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReceiptsList } from "@/components/receipts/ReceiptsList";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";

const formatTime12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  const ampm = hh >= 12 ? "PM" : "AM";
  const display = hh % 12 || 12;
  return `${display}:${m} ${ampm}`;
};

const STATUS_COLORS = {
  paid: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-700",
  pending: "bg-orange-100 text-orange-700",
  cancelled: "bg-red-100 text-red-700",
  series: "bg-slate-100 text-slate-600",
};

/** Customer rental history dialog — Overview / Bookings / Payments / Timeline */
export const CustomerHistoryDialog = ({ open, onOpenChange, customer }) => {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    if (!open || !customer?.customer_id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/customers/${customer.customer_id}/history`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Failed to load history"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, customer?.customer_id, refreshTick]);

  if (!customer) return null;

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "bookings", label: `Bookings${data ? ` (${data.bookings?.length || 0})` : ""}` },
    { key: "payments", label: "Payments" },
    { key: "timeline", label: "Timeline" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="customer-history-dialog"
        className="max-w-3xl w-[96vw] sm:w-[92vw] max-h-[92vh] p-0 overflow-hidden gap-0"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-lg sm:text-xl">{customer.name}</DialogTitle>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-slate-500">
            {customer.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phone}</span>}
            {customer.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</span>}
            {customer.company_name && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{customer.company_name}</span>}
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 px-5 overflow-x-auto scrollbar-hide bg-white border-b border-slate-100 shrink-0">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`customer-history-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-3 text-xs font-medium whitespace-nowrap border-b-2 leading-5 transition ${
                tab === t.key
                  ? "border-violet-600 text-violet-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto" style={{ maxHeight: "70vh" }}>
          <div className="p-5">
            {loading && !data ? (
              <p className="text-center text-sm text-slate-500 py-8">Loading history…</p>
            ) : (
              <>
                {tab === "overview" && <OverviewTab customer={customer} />}
                {tab === "bookings" && <BookingsTab data={data} onRefresh={refresh} />}
                {tab === "payments" && <PaymentsTab data={data} />}
                {tab === "timeline" && <TimelineTab data={data} />}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================
// Overview Tab
// =============================================================

const OverviewTab = ({ customer }) => (
  <div className="space-y-3 text-sm" data-testid="customer-overview">
    <Row icon={<Phone className="w-4 h-4 text-slate-400" />} label="Phone" value={customer.phone || "—"} />
    {customer.email && <Row icon={<Mail className="w-4 h-4 text-slate-400" />} label="Email" value={customer.email} />}
    {customer.company_name && <Row icon={<Building2 className="w-4 h-4 text-slate-400" />} label="Company" value={customer.company_name} />}
    {customer.gst_number && <Row icon={<FileText className="w-4 h-4 text-slate-400" />} label="GST" value={customer.gst_number} />}
    {customer.address && <Row icon={<MapPin className="w-4 h-4 text-slate-400" />} label="Address" value={customer.address} />}
  </div>
);

const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
    {icon}
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-900 break-words">{value}</p>
    </div>
  </div>
);

// =============================================================
// Bookings Tab — with filters, recurring rollup, nested receipts, record payment
// =============================================================

const BookingsTab = ({ data, onRefresh }) => {
  const bookings = data?.bookings || [];
  const groups = data?.groups || [];

  const [statusFilter, setStatusFilter] = useState(new Set()); // empty = show all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const groupById = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.recurring_group_id] = g;
    return m;
  }, [groups]);

  // Roll children up to series + keep singles
  const items = useMemo(() => {
    const seenSeries = new Set();
    const out = [];
    for (const b of bookings) {
      const gid = b.recurring_group_id;
      if (gid) {
        if (seenSeries.has(gid)) continue;
        seenSeries.add(gid);
        const g = groupById[gid];
        if (g) {
          const totalAmt = g.total_amount || 0;
          const paidAmt = g.amount_paid || 0;
          const balAmt = g.balance_amount ?? Math.max(0, totalAmt - paidAmt);
          const derivedStatus = balAmt <= 0 && totalAmt > 0 ? "paid" : paidAmt > 0 ? "partial" : "pending";
          out.push({
            kind: "series",
            id: gid,
            booking_date: g.start_date || b.booking_date,
            studio_name: g.studio_name || b.studio_name,
            room_name: g.room_name || b.room_name,
            start_time: g.start_time || b.start_time,
            end_time: g.end_time || b.end_time,
            booking_type: "Recurring",
            total_amount: totalAmt,
            amount_paid: paidAmt,
            balance_amount: balAmt,
            payment_status: g.payment_status || derivedStatus,
            instances_count: g.instances_count,
            cancelled_count: g.cancelled_count,
            children: bookings.filter((x) => x.recurring_group_id === gid),
            recurring_group_id: gid,
            created_by_name: b.created_by_name,
          });
        }
      } else {
        out.push({
          kind: "single",
          id: b.booking_id,
          booking_id: b.booking_id,
          booking_date: b.booking_date,
          studio_name: b.studio_name,
          room_name: b.room_name,
          start_time: b.start_time,
          end_time: b.end_time,
          booking_type: b.booking_type || "Rental",
          total_amount: b.total_amount || b.agreed_rent || 0,
          amount_paid: b.amount_paid || 0,
          balance_amount: b.balance_amount ?? Math.max(0, (b.total_amount || b.agreed_rent || 0) - (b.amount_paid || 0)),
          payment_status: b.status === "cancelled" ? "cancelled" : (b.rent_payment_status || "pending"),
          status: b.status,
          created_by_name: b.created_by_name,
          payment_history: b.payment_history || [],
        });
      }
    }
    return out;
  }, [bookings, groupById]);

  // Apply filters
  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (statusFilter.size > 0 && !statusFilter.has(it.payment_status)) return false;
      if (dateFrom && it.booking_date && new Date(it.booking_date) < new Date(dateFrom)) return false;
      if (dateTo && it.booking_date && new Date(it.booking_date) > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }, [items, statusFilter, dateFrom, dateTo]);

  const toggleStatus = (s) => {
    const next = new Set(statusFilter);
    if (next.has(s)) next.delete(s); else next.add(s);
    setStatusFilter(next);
  };

  const clearFilters = () => { setStatusFilter(new Set()); setDateFrom(""); setDateTo(""); };
  const filtersActive = statusFilter.size > 0 || dateFrom || dateTo;

  return (
    <div className="space-y-3" data-testid="customer-bookings-tab">
      {/* Filters — sticky, breaks out of parent padding so it spans full dialog width */}
      <div className="-mx-5 -mt-5 sticky top-0 z-20 bg-white border-b border-slate-100">
        <div className="flex flex-wrap items-center gap-2 px-5 py-3">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-semibold uppercase tracking-wide">
            <Filter className="w-3 h-3" /> Filters
          </span>
          {["pending", "partial", "paid", "cancelled"].map((s) => (
            <button
              key={s}
              data-testid={`customer-filter-${s}`}
              onClick={() => toggleStatus(s)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                statusFilter.has(s)
                  ? "bg-violet-600 border-violet-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-violet-300"
              }`}
            >
              {s}
            </button>
          ))}
          <div className="flex items-center gap-1">
            <Input
              data-testid="customer-filter-date-from"
              type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 text-xs rounded-lg w-[130px]"
            />
            <span className="text-slate-400 text-xs">→</span>
            <Input
              data-testid="customer-filter-date-to"
              type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-8 text-xs rounded-lg w-[130px]"
            />
          </div>
          {filtersActive && (
            <button onClick={clearFilters} className="text-[11px] text-violet-600 hover:underline ml-auto">
              Clear
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-sm text-slate-500 py-8" data-testid="customer-bookings-empty">
          {items.length === 0 ? "No bookings yet." : "No bookings match the current filters."}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <BookingCard key={item.id} item={item} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
};

const BookingCard = ({ item, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);

  const isSeries = item.kind === "series";
  const isCancelled = item.payment_status === "cancelled" || item.status === "cancelled";
  const canRecord = !isCancelled && item.balance_amount > 0;
  const statusKey = isCancelled ? "cancelled" : item.payment_status;

  return (
    <div
      className={`rounded-xl border ${isCancelled ? "border-red-200 bg-red-50/30" : "border-slate-200 bg-white"}`}
      data-testid={`customer-booking-${item.id}`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-slate-900">
                {item.studio_name || "Studio"} · {item.room_name || "Room"}
              </span>
              {isSeries && (
                <span className="inline-flex items-center gap-1 text-[10px] text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full font-semibold">
                  <RefreshCw className="w-2.5 h-2.5" /> Series · {item.instances_count}{item.cancelled_count > 0 ? ` (${item.cancelled_count} cancelled)` : ""}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[statusKey] || "bg-slate-100 text-slate-600"}`}>
                {(statusKey || "pending").toUpperCase()}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{item.booking_date ? format(new Date(item.booking_date), "dd MMM yyyy") : "—"}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime12(item.start_time)} – {formatTime12(item.end_time)}</span>
              <span>{item.booking_type}</span>
            </div>
          </div>
        </div>

        {/* Money strip */}
        <div className="mt-2.5 grid grid-cols-3 gap-2 text-xs">
          <Money label="Booking Amount" value={item.total_amount} />
          <Money label="Amount Paid" value={item.amount_paid} good />
          <Money label="Pending" value={item.balance_amount} bad />
        </div>

        {item.created_by_name && (
          <p className="text-[10px] text-slate-400 mt-2">Created by {item.created_by_name}</p>
        )}

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {canRecord && (
            <Button
              size="sm"
              data-testid={`record-rent-${item.id}`}
              onClick={() => setRecordOpen(true)}
              className="h-8 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Record Payment
            </Button>
          )}
          <Button
            size="sm" variant="ghost"
            onClick={() => setExpanded((e) => !e)}
            className="h-8 rounded-full text-violet-700 hover:bg-violet-50 text-xs ml-auto"
            data-testid={`expand-booking-${item.id}`}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5 mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}
            {expanded ? "Hide details" : "Receipts & details"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/40 p-3 space-y-3">
          {/* Receipts */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Receipts</p>
            <ReceiptsList
              {...(isSeries ? { recurringGroupId: item.recurring_group_id } : { bookingId: item.booking_id })}
              refreshKey={item.amount_paid || 0}
            />
          </div>

          {/* Series occurrences breakdown */}
          {isSeries && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Occurrences ({item.children?.length || 0})
              </p>
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {(item.children || []).map((c) => (
                  <div key={c.booking_id} className="flex items-center justify-between text-xs px-2 py-1 rounded-md bg-white border border-slate-100">
                    <span>
                      #{c.recurrence_instance_number} · {c.booking_date ? format(new Date(c.booking_date), "dd MMM") : "—"} · {formatTime12(c.start_time)}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${STATUS_COLORS[c.status === "cancelled" ? "cancelled" : (c.rent_payment_status || "pending")] || "bg-slate-100"}`}>
                      {(c.status === "cancelled" ? "cancelled" : (c.rent_payment_status || "pending")).toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Inline record-payment dialog */}
      {recordOpen && (
        <RecordRentPaymentDialog
          item={item}
          onClose={() => setRecordOpen(false)}
          onSaved={() => { setRecordOpen(false); onRefresh(); }}
        />
      )}
    </div>
  );
};

const Money = ({ label, value, good, bad }) => (
  <div className={`rounded-lg px-2 py-1.5 ${bad ? "bg-red-50" : good ? "bg-green-50" : "bg-slate-50"}`}>
    <p className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className={`text-xs font-bold ${bad ? "text-red-700" : good ? "text-green-700" : "text-slate-900"}`}>
      ₹{Number(value || 0).toLocaleString("en-IN")}
    </p>
  </div>
);

// =============================================================
// Inline Record Rent Payment Dialog
// =============================================================

const RecordRentPaymentDialog = ({ item, onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > item.balance_amount) { toast.error(`Amount cannot exceed ₹${item.balance_amount}`); return; }
    setSaving(true);
    try {
      const url = item.kind === "series"
        ? `/rent-payments/by-group/${item.recurring_group_id}/record`
        : `/rent-payments/${item.booking_id}/record`;
      await api.post(url, {
        amount: amt, payment_method: method,
        payment_date: date, reference_number: reference || null, notes: notes || null,
      });
      toast.success(`₹${amt} recorded successfully`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md w-[92vw]" data-testid="record-rent-payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-heading">Record Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 rounded-lg px-3 py-2 flex justify-between text-xs">
            <span className="text-slate-500">Outstanding Balance</span>
            <span className="font-bold text-red-600">₹{item.balance_amount}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Amount *</Label>
              <Input data-testid="rrp-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Max ₹${item.balance_amount}`} className="h-9 rounded-lg text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-9 rounded-lg border px-2 bg-white text-sm">
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-lg text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reference #</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" className="h-9 rounded-lg text-sm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className="h-9 rounded-lg text-sm" />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="w-full sm:flex-1 rounded-full" disabled={saving}>Cancel</Button>
            <Button data-testid="rrp-confirm" onClick={submit} disabled={saving} className="w-full sm:flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {saving ? "Recording…" : `Record ₹${amount || 0}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================
// Payments Tab — flat list of all transactions
// =============================================================

const PaymentsTab = ({ data }) => {
  const txns = useMemo(() => {
    const out = [];
    for (const rp of data?.rent_payments || []) {
      const groupOrBooking = rp.recurring_group_id ? `Series ${rp.recurring_group_id.slice(0, 8)}` : `Booking`;
      for (const t of rp.payment_history || []) {
        out.push({ ...t, _ctx: groupOrBooking, _rp: rp });
      }
    }
    return out.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [data]);

  if (!txns.length) {
    return <p className="text-center text-sm text-slate-500 py-8" data-testid="customer-payments-empty">No payments recorded yet.</p>;
  }

  return (
    <div className="space-y-2" data-testid="customer-payments-tab">
      {txns.map((t, i) => (
        <div key={`${t.date || ""}-${t.amount}-${t.method || ""}-${i}`} className="p-3 bg-slate-50 rounded-xl text-sm">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="font-semibold text-green-700">₹{Number(t.amount || 0).toLocaleString("en-IN")}</span>
            <span className="text-xs text-slate-500">{t.date ? format(new Date(t.date), "dd MMM yyyy") : "—"}</span>
          </div>
          <div className="flex gap-3 text-xs text-slate-500 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <CreditCard className="w-3 h-3" />
              {(t.method || "—").toString().toUpperCase()}
            </span>
            {t.reference_number && <span>Ref: {t.reference_number}</span>}
            <span className="text-slate-400">{t._ctx}</span>
          </div>
          {t.notes && <p className="text-xs text-slate-400 mt-1">{t.notes}</p>}
        </div>
      ))}
    </div>
  );
};

// =============================================================
// Timeline Tab — derived chronological events
// =============================================================

const TimelineTab = ({ data }) => {
  const events = useMemo(() => {
    const out = [];
    // Booking created
    for (const b of data?.bookings || []) {
      if (b.created_at) {
        out.push({
          type: "booking",
          icon: Calendar,
          color: "violet",
          ts: b.created_at,
          title: `Booking created · ${b.studio_name || "—"}`,
          subtitle: `${b.booking_date ? format(new Date(b.booking_date), "dd MMM yyyy") : "—"}, ${formatTime12(b.start_time)}–${formatTime12(b.end_time)}`,
        });
      }
      if (b.cancelled_at) {
        out.push({
          type: "cancel",
          icon: Calendar,
          color: "red",
          ts: b.cancelled_at,
          title: "Booking cancelled",
          subtitle: b.studio_name || "",
        });
      }
    }
    // Payments
    for (const rp of data?.rent_payments || []) {
      for (const t of rp.payment_history || []) {
        out.push({
          type: "payment",
          icon: CreditCard,
          color: "green",
          ts: t.date,
          title: `Payment received · ₹${Number(t.amount || 0).toLocaleString("en-IN")}`,
          subtitle: `${(t.method || "—").toUpperCase()}${t.reference_number ? ` · Ref ${t.reference_number}` : ""}`,
        });
      }
    }
    // Receipts
    for (const r of data?.receipts || []) {
      out.push({
        type: "receipt",
        icon: FileText,
        color: "blue",
        ts: r.created_at,
        title: `Receipt issued · ${r.receipt_number}`,
        subtitle: `₹${Number(r.transaction_amount || 0).toLocaleString("en-IN")}`,
      });
    }
    return out
      .filter((e) => e.ts)
      .sort((a, b) => new Date(b.ts) - new Date(a.ts));
  }, [data]);

  const colorMap = {
    violet: "bg-violet-100 text-violet-700",
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
  };

  if (!events.length) {
    return <p className="text-center text-sm text-slate-500 py-8" data-testid="customer-timeline-empty">No timeline events.</p>;
  }

  return (
    <div className="space-y-2" data-testid="customer-timeline-tab">
      {events.map((e, i) => {
        const Ico = e.icon;
        return (
          <div key={`${e.ts || ""}-${e.title || ""}-${i}`} className="flex gap-3 items-start p-2.5 rounded-lg hover:bg-slate-50">
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${colorMap[e.color]}`}>
              <Ico className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{e.title}</p>
              <p className="text-xs text-slate-500 truncate">{e.subtitle}</p>
            </div>
            <span className="text-[10px] text-slate-400 shrink-0">{format(new Date(e.ts), "dd MMM yyyy")}</span>
          </div>
        );
      })}
    </div>
  );
};

export default CustomerHistoryDialog;
